/**
 * Which streams are actually on screen — declared by the renderer, not guessed.
 *
 * The cap that pauses off-screen video was disabled by default, so a phone in a
 * twenty-person call rendered six tiles and consumed twenty streams at the top
 * rung. Turning it on looked like a one-line change and is not: the obvious fix
 * is to slice the priority-ordered peer list, but `VideoGrid` does its OWN
 * paging with its own page size (and a six-tile cap on touch devices), from the
 * FULL roster. Any set re-derived outside that subtree pauses people who are on
 * screen, and their tiles freeze on the last decoded frame.
 *
 * So nothing here re-derives anything. Every camera and extra-camera tile goes
 * through `VideoTile`, so mounting IS the fact of being on screen — pagination,
 * the touch cap, the overflow group tile, panel opens and layout switches all
 * come for free, with no copy of the layout maths to drift.
 *
 * Refcounted, because one source can legitimately be on screen twice at once.
 */

/** Keys are the shared `peer:source:kind` triple — never built here. */
const rendered = new Map<string, number>()
const lingering = new Map<string, ReturnType<typeof setTimeout>>()
const consumerKeys = new Set<string>()
/**
 * Keys the SFU has ANNOUNCED but we have not consumed yet (LAZY_CONSUME).
 *
 * A tile reporting a key with no consumer is normally drift — two sides
 * disagreeing about spelling — and the check below disengages the cap rather
 * than freeze that tile. Under lazy consume it is not drift at all: it is the
 * designed steady state for the moment between a tile mounting and its stream
 * being asked for. Without this distinction the safety net fires on every
 * lazily-consumed tile, the cap fails open, and the request that would fix it
 * is itself gated on the cap — which is exactly how lazy consume broke video.
 */
const awaitingConsumer = new Set<string>()
const listeners = new Set<() => void>()

let snapshot: ReadonlySet<string> | null = null
/**
 * Cached separately from `snapshot`, and it must be cached at all:
 * `useSyncExternalStore` compares successive snapshots with Object.is, so a
 * getter that builds a fresh Set each call reports a change on every render
 * and spins for ever.
 */
let renderedSnapshot: ReadonlySet<string> | null = null
let drift = false

/**
 * A tile that has just scrolled off keeps its stream for this long.
 *
 * Paging back and forth is common and a pause/resume round trip costs a
 * keyframe and a visible stall, so the resume side is immediate and only the
 * pause side waits.
 */
const PAUSE_GRACE_MS = 2000

/**
 * How long a rendered key may have no matching consumer before we call it drift.
 *
 * Generous on purpose: a tile legitimately mounts before its consumer exists.
 */
const DRIFT_GRACE_MS = 5000

function notify(): void {
  snapshot = null
  renderedSnapshot = null
  listeners.forEach((fn) => fn())
}

/**
 * The set of keys that should stay live, or null meaning "no cap — resume
 * everything".
 *
 * Null is returned before anything has painted, and permanently once drift is
 * detected. Both are fail-open: the cost of not capping is bandwidth, the cost
 * of capping wrongly is a frozen tile that looks like a broken call.
 */
export function getSnapshot(): ReadonlySet<string> | null {
  if (drift) return null
  if (snapshot) return snapshot
  if (rendered.size === 0 && lingering.size === 0) return null
  snapshot = new Set([...rendered.keys(), ...lingering.keys()])
  return snapshot
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/**
 * Declare that this key is on screen. Returns the release.
 *
 * A null key (a local preview, which has no consumer) is a no-op, so callers
 * never have to branch.
 */
export function retain(key: string | null): () => void {
  if (!key) return () => {}

  const linger = lingering.get(key)
  if (linger) { clearTimeout(linger); lingering.delete(key) }

  rendered.set(key, (rendered.get(key) ?? 0) + 1)
  scheduleDriftCheck(key)
  notify()

  let released = false
  return () => {
    if (released) return
    released = true
    const n = (rendered.get(key) ?? 1) - 1
    if (n > 0) { rendered.set(key, n); notify(); return }
    rendered.delete(key)
    lingering.set(key, setTimeout(() => { lingering.delete(key); notify() }, PAUSE_GRACE_MS))
    notify()
  }
}

/** This key is announced and a consume is expected; do not call it drift. */
export function markAwaitingConsumer(key: string): void {
  awaitingConsumer.add(key)
}

/** The consume landed (or the stream went away) — stop excusing this key. */
export function clearAwaitingConsumer(key: string): void {
  awaitingConsumer.delete(key)
}

/**
 * Every key currently on screen, regardless of the cap.
 *
 * `getSnapshot` answers "what should stay live", and returns null both before
 * anything paints and permanently after drift — it is the CAP's output, and
 * null means "do not cap". That is the wrong thing to drive a fetch from: a
 * stream that is not being consumed cannot be capped, and a null would skip
 * exactly the tiles that need asking for. This is the raw fact instead, and it
 * is never null.
 */
export function getRendered(): ReadonlySet<string> {
  if (!renderedSnapshot) {
    renderedSnapshot = new Set([...rendered.keys(), ...lingering.keys()])
  }
  return renderedSnapshot
}

/** Consumers announce themselves so a key that matches nothing can be spotted. */
export function registerConsumer(key: string): void {
  consumerKeys.add(key)
}

export function unregisterConsumer(key: string): void {
  consumerKeys.delete(key)
}

/**
 * The loud failure.
 *
 * If a tile keeps reporting a key no consumer ever registers, while some OTHER
 * consumer exists for the same peer, the two sides disagree about spelling
 * rather than about existence — which is precisely how layer selection was
 * silently inert for extra cameras. The consequence here would be worse: that
 * tile gets paused and freezes.
 *
 * So we say so, and disengage the cap entirely rather than freeze anyone.
 */
function scheduleDriftCheck(key: string): void {
  if (drift || consumerKeys.has(key) || awaitingConsumer.has(key)) return
  setTimeout(() => {
    if (drift || !rendered.has(key) || consumerKeys.has(key)) return
    // Announced but not yet consumed is the designed state under lazy consume,
    // not a spelling disagreement. Re-checked here rather than only above
    // because the announcement can arrive during the grace.
    if (awaitingConsumer.has(key)) return
    const peer = key.split(':')[0]
    const siblings = [...consumerKeys].filter((k) => k.split(':')[0] === peer)
    if (siblings.length === 0) return   // peer genuinely has no consumer yet
    drift = true
    console.error(
      '[rendered-tiles] key drift — a tile is reporting a key no consumer uses. ' +
      'Pausing is disabled for this session rather than freezing that tile.',
      { renderedKey: key, consumersForSamePeer: siblings },
    )
    notify()
  }, DRIFT_GRACE_MS)
}

/** Test seams. */
export function __reset(): void {
  rendered.clear()
  lingering.forEach((t) => clearTimeout(t))
  lingering.clear()
  consumerKeys.clear()
  awaitingConsumer.clear()
  listeners.clear()
  snapshot = null
  renderedSnapshot = null
  drift = false
}
export function __drifted(): boolean { return drift }
export const __PAUSE_GRACE_MS = PAUSE_GRACE_MS
export const __DRIFT_GRACE_MS = DRIFT_GRACE_MS
